import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { menuItems } from 'src/components/layouts/Nav/routes';
import { useAuthToken } from 'src/hooks/useAuthHook';
import { $api } from 'src/services/api';
import { fetchAuthenticatedProfile } from 'src/services/authProfile';
import { clearTokenIfCurrent } from 'src/services/authToken';
import logger from 'src/utils/logger';

/**
 * Static prefix of a route pattern: everything up to the first `:param` / `*`
 * segment. `/tracking/auth/:orderNumber` → `/tracking/auth`, `/return/:orderId/`
 * → `/return`, `/login/` → `/login`.
 *
 * Used by `App` to derive the public paths handed to `<Auth publicPaths>` from
 * the route table itself, so the guard cannot fall out of sync with the routes.
 */
export const routePathPrefix = (path: string): string => {
  const staticSegments: string[] = [];
  path
    .split('/')
    .filter(Boolean)
    .every(segment => {
      if (segment.startsWith(':') || segment === '*') return false;
      staticSegments.push(segment);
      return true;
    });
  return `/${staticSegments.join('/')}`;
};

/**
 * Exact / prefix match instead of `pathname.includes(route)`. `includes` also
 * matched garbage like `/warehouse/inbound/login`, which then rendered nothing
 * and never corrected itself — a permanently blank page that only a manual
 * reload resolved.
 */
export const matchesPublicPath = (pathname: string, publicPaths: string[]) =>
  publicPaths.some(route => pathname === route || pathname.startsWith(`${route}/`));

type NavEntry = { link?: string; childs?: { link?: string }[] };

const NAV_ROUTES: string[] = (menuItems as NavEntry[])
  .flatMap(item => [item.link, ...(item.childs ?? []).map(child => child.link)])
  .filter((link): link is string => Boolean(link) && !link!.includes('?'))
  .map(link => `/${link}`);

const SETTINGS_TABS_FOR_EVERYONE = ['', 'my_profile', 'company', 'cases', 'faq_help', 'whats_new'];

type AccessRight = { route: string; read: boolean; read_write: boolean };

export const settingsTabAllowed = (tab: string, rights: AccessRight[]): boolean => {
  if (SETTINGS_TABS_FOR_EVERYONE.includes(tab)) return true;
  if (tab === 'forecast_planning') {
    return rights.some(right => right.route === '/forecasts' && (right.read || right.read_write));
  }
  if (tab === 'contacts') {
    return rights.some(right => right.route === '/contact' && (right.read || right.read_write));
  }
  return rights.some(right => right.route.includes(tab) && (right.read || right.read_write));
};

export const isUserManagementPage = (pathname: string): boolean =>
  pathname === '/settings/create' || pathname.startsWith('/settings/edit/');

export const navRouteFor = (pathname: string): string | undefined =>
  NAV_ROUTES.filter(route => pathname === route || pathname.startsWith(`${route}/`)).sort(
    (a, b) => b.length - a.length,
  )[0];

const Auth = ({ children, publicPaths }: { children: any; publicPaths: string[] }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Subscribed, not polled: the guard reacts to a token disappearing (logout in
  // this tab, logout in another tab, an expired token being dropped by a 401
  // handler) without any component having to navigate on its behalf.
  const token = useAuthToken();

  // Whether this guard ever completed a successful profile check. Used to keep
  // a transient backend/network failure from throwing an already signed-in user
  // out of the app.
  //
  // Known and accepted limitation: the ref starts at `false` on every mount, so
  // a *reload* while the backend is down sends a perfectly valid session to
  // `/login` (the token survives, only the view is wrong). Rendering a
  // "backend unreachable, retrying" state instead would be the better answer,
  // but it needs its own copy in de/en and a retry loop — a separate change,
  // not something to smuggle into an auth fix. Until then `/login` is at least
  // an actionable screen, and after a successful check the flag protects every
  // subsequent navigation.
  const wasAuthenticatedRef = useRef(false);

  // Same query key as `useUserAccessRightsQuery()` (init === undefined), so this
  // shares the cache entry with `useNavigationItems` instead of duplicating the
  // request. Called through `$api` directly because `enabled` has to be set
  // here: previously the query fired unconditionally on mount, before any token
  // check had run.
  const { data: accessRights, isLoading: isAccessLoading } = $api.useQuery(
    'get',
    '/users/user/access_rights/',
    undefined,
    { enabled: !!token },
  );

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const redirectToLogin = () => {
      if (!matchesPublicPath(location.pathname, publicPaths)) {
        navigate('/login', { replace: true });
      }
    };

    const checkAuth = async () => {
      if (!token) {
        setIsAuthenticated(false);
        wasAuthenticatedRef.current = false;
        logger.warn('No auth token found, redirecting to login');
        redirectToLogin();
        return;
      }

      try {
        const response = await fetchAuthenticatedProfile(controller.signal);
        if (cancelled) return;

        if (!response) {
          // Token vanished while the request was being prepared.
          setIsAuthenticated(false);
          wasAuthenticatedRef.current = false;
          redirectToLogin();
          return;
        }

        if (response.ok) {
          wasAuthenticatedRef.current = true;
          setIsAuthenticated(true);
          return;
        }

        if (response.status === 401) {
          // Genuinely rejected credentials — and only here is the token thrown
          // away, and only when storage still holds the very token this check
          // used. The previous version deleted it on *any* failure, including
          // a 502 or a network blip.
          logger.warn('Auth check rejected the token');
          setIsAuthenticated(false);
          wasAuthenticatedRef.current = false;
          clearTokenIfCurrent(token);
          redirectToLogin();
          return;
        }

        // 5xx / gateway hiccup: the token is probably still fine, so keep it.
        logger.error('Auth check failed', response.status);
        if (!wasAuthenticatedRef.current) {
          setIsAuthenticated(false);
          redirectToLogin();
        }
      } catch (e) {
        if (cancelled || controller.signal.aborted) return;
        // Network / CORS error — same reasoning: never drop the token for it.
        logger.error('Auth check failed', e);
        if (!wasAuthenticatedRef.current) {
          setIsAuthenticated(false);
          redirectToLogin();
        }
      }
    };

    checkAuth();

    return () => {
      // Mounted guard: a response arriving after unmount (or after the next
      // check superseded this one) must not write state or redirect.
      cancelled = true;
      controller.abort();
    };
    // `publicPaths` by value, not by identity: an inline array literal from a
    // caller must not re-run the auth check on every render.
  }, [token, location.pathname, publicPaths.join('|')]);

  const matchesAccessRoute = (route: string, currentPath: string, currentParams: URLSearchParams) => {
    const [routePath, routeQuery] = route.split('?');
    if (routeQuery) {
      if (currentPath !== routePath) return false;
      return Array.from(new URLSearchParams(routeQuery)).every(([key, value]) => currentParams.get(key) === value);
    }
    return currentPath === routePath || currentPath.startsWith(`${routePath}/`);
  };

  const handleAccessControl = () => {
    if (isAuthenticated && !isAccessLoading && accessRights) {
      const { pathname, search } = location;

      const searchParams = new URLSearchParams(search);
      const tab = searchParams.get('tab') ?? '';
      const navRoute = navRouteFor(pathname);

      let hasAccess: boolean;
      if (pathname.startsWith('/settings/contacts')) {
        hasAccess = settingsTabAllowed('contacts', accessRights);
      } else if (pathname === '/settings') {
        hasAccess = settingsTabAllowed(tab, accessRights);
      } else if (isUserManagementPage(pathname)) {
        hasAccess = settingsTabAllowed('manage_users', accessRights);
      } else if (navRoute) {
        hasAccess =
          navRoute === '/dashboard' ||
          accessRights.some(item => item.route === navRoute && (item.read || item.read_write));
      } else {
        hasAccess = accessRights.some(item => matchesAccessRoute(item.route, pathname, searchParams));
      }

      if (!hasAccess) {
        logger.warn('Access denied for route, redirecting to dashboard', pathname);
        navigate('/dashboard', { replace: true });
      }
    }
  };

  useEffect(() => {
    handleAccessControl();
  }, [isAuthenticated, isAccessLoading, accessRights, location.pathname]);

  // `token` is synchronous (`useSyncExternalStore`), `isAuthenticated` is state
  // written from an effect — so on logout there is necessarily one *committed*
  // render with `token === null && isAuthenticated === true`. Gating on both
  // keeps the protected tree from re-mounting its queries in exactly that
  // render (each of which then short-circuits with "no access token").
  return isAuthenticated && token ? children : null;
};

export default Auth;
